import { useMemo, useState } from "react";

import { useMutation, useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse, AxiosError } from "axios";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../constants";
import { useAppState } from "../../context/StateContext";
import { Booking } from "../../models/booking";
import { CartonLog } from "../../models/carton.log";
import { Error } from "../../models/error";
import { Filters, Searches } from "../../models/shipeezi.table";
import { ShipmentDocument } from "../../models/shipment.document";
import CartonLogService from "../../services/carton.log.service";
import CartonService from "../../services/carton.service";
import PurchaseOrderService from "../../services/purchase.order.service";
import ShipmentBookingService from "../../services/shipment.booking.service";
import ShipmentDocumentService from "../../services/shipment.document.service";
import ShipmentReportService from "../../services/shipment.report.service";
import ShipmentService from "../../services/shipment.service";
import { checkError } from "../../util/checkError";
import { keysToCamel } from "../../util/convertCamelCase";
import { TypeError } from "../../util/enums";
import { handleGeneratePdf } from "../../util/generateReport";
import { BookingKeys } from "../../util/queries";
import { NotificationToast } from "../Generic/Toast/NotificationToast";
import { useBookingContext } from "./context/Booking";
import { BookingChangeKeys } from "./util";

interface SaveBookingProps {
  id?: number;
  booking: Booking;
  saveAndBook?: boolean;
  cfsReceived?: boolean;
  received?: boolean;
  overrideChangedOptions?: BookingChangeKeys;
  uuid?: string;
}

interface UpdateShipmentDocumentProps {
  shipmentId: number;
  documents?: ShipmentDocument[];
}

interface ImportBookingProps {
  file: any;
  userId: string;
}

interface ReportAllDocumentsProps {
  shipmentId: number;
  hasBookingId: boolean;
  documents?: ShipmentDocument[];
}

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useActionBooking = () => {
  const { changedOptions, setResponseError } = useBookingContext();
  const { currentUser, isFMVersion } = useAppState();
  const [requestingSaveBooking, setRequestingSaveBooking] = useState(false);

  const queryClient = useQueryClient();

  const projects = useMemo(() => currentUser?.projects, [currentUser]);
  const defaultProjectId = useMemo(() => {
    if (projects && projects?.length) return Number(projects[0]?.id || 0);

    return 0;
  }, [projects]);
  const defaultEventType = useMemo(() => {
    if (projects && projects?.length) return String(projects[0]?.name || "");

    return "";
  }, [projects]);

  const { t: translate } = useTranslation();

  const history = useHistory();

  const getAllBookings = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: BookingKeys.getAllNetwork([
        currentUser?.companyId,
        page,
        pageSize,
        searches,
        filters,
      ]),
      queryFn: async () => {
        const response = await ShipmentBookingService.getAllNetwork({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          isBooking: 1,
          companyId: currentUser?.companyId,
          group: ["shipment.id"],
          attributes: [
            "id",
            "containerId",
            "origin",
            "destination",
            "shipmentType",
            "statusShipment",
            "companyId",
            "totalQuantity",
          ],
          include: JSON.stringify([
            { association: "purchaseOrders", attributes: ["reference"] },
            {
              association: "company",
              attributes: ["id"],
              include: [{ association: "supplierAssociated", attributes: ["name"] }],
            },
            { association: "consignee", attributes: ["fullName", "name", "lastName"] },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getBookingById = (bookingId: number, callerCallback?: (bookingId: number) => void) => {
    if (bookingId && callerCallback) callerCallback(bookingId);

    return useQuery({
      queryKey: BookingKeys.getById(bookingId),
      queryFn: async () => {
        const response = await ShipmentService.getById(Number(bookingId), {
          include: JSON.stringify([
            {
              association: "portOfLoading",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            {
              association: "portOfDestination",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            {
              association: "portOfDischarge",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            {
              association: "portOfReceipt",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            { association: "marks", attributes: ["line", "description", "mark", "shipmentId"] },

            {
              association: "commercialInvoice",
              include: [{ association: "bankAddress" }, { association: "beneficiaryAddress" }],
            },
            { association: "notifyParty" },
            { association: "office" },
            {
              association: "consignee",
              attributes: ["code", "fullName", "name", "lastName"],
            },
            { association: "containers" },
          ]),
        });

        if (response && response.data) return response.data as Booking;
      },
      enabled: !!bookingId,
      cacheTime: CACHE_TIME,
    });
  };

  const getCartonsByBooking = (bookingId: number, callerCallback?: (bookingId: number) => void) => {
    if (bookingId && callerCallback) callerCallback(bookingId);

    return useQuery({
      queryKey: BookingKeys.getCartons(bookingId),
      queryFn: async () => {
        const response = await CartonService.getAll({
          shipmentId: bookingId,
          include: JSON.stringify([
            {
              association: "cartonProducts",
              include: [
                { association: "purchaseOrder", required: true },
                { association: "product", required: true },
                { association: "purchaseOrderProduct", required: true, paranoid: false },
              ],
            },
            {
              association: "comments",
              include: [
                {
                  association: "user",
                  required: true,
                  attributes: ["fullName", "pictureId"],
                  include: [{ association: "picture", attributes: ["mediaLink"] }],
                },
              ],
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!bookingId,
      cacheTime: 60 * 60 * 1000, // 1 hour
    });
  };

  const getOrdersByBooking = (bookingId: number, callerCallback?: (bookingId: number) => void) => {
    if (bookingId && callerCallback) callerCallback(bookingId);

    return useQuery({
      queryKey: BookingKeys.getOrders(bookingId),
      queryFn: async () => {
        const response = await PurchaseOrderService.getAll({
          include: JSON.stringify([
            {
              association: "bookings",
              where: {
                id: bookingId,
              },
              required: true,
            },
            {
              association: "consignee",
            },
            {
              association: "supplier",
              include: [{ association: "location" }],
            },
            {
              association: "purchaseOrderProducts",
              include: [{ association: "product" }],
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!bookingId,
      cacheTime: CACHE_TIME,
    });
  };

  const getExportBookingsById = (
    setBookings: React.Dispatch<React.SetStateAction<Booking[]>>,
    bookingIds: string[] | number[]
  ) => {
    return useQueries({
      queries: bookingIds.map((bookingId) => {
        return {
          queryKey: BookingKeys.getExportById(Number(bookingId)),
          queryFn: () => ShipmentService.getExportById(Number(bookingId)),
          enabled: !!bookingId,
          refetchOnWindowFocus: false,
          onSuccess: (response: AxiosResponse<Booking[]>) => {
            if (response && response.data) {
              setBookings((olBookings) => [...olBookings, keysToCamel(response.data[0])]);
            }
          },
        };
      }),
    });
  };

  const getBookingsById = (
    setBookings: React.Dispatch<React.SetStateAction<Booking[]>>,
    bookingIds: string[] | number[]
  ) => {
    return useQueries({
      queries: bookingIds.map((bookingId) => {
        return {
          queryKey: BookingKeys.getById(Number(bookingId)),
          queryFn: () => ShipmentService.getById(Number(bookingId)),
          enabled: !!bookingId,
          refetchOnWindowFocus: false,
          onSuccess: (response: AxiosResponse<Booking>) => {
            if (response && response.data) {
              setBookings((oldBookings) => [...oldBookings, keysToCamel(response.data)]);
            }
          },
        };
      }),
    });
  };

  const useImportBooking = () => {
    return useMutation({
      mutationKey: BookingKeys.import(),
      mutationFn: ({ file, userId }: ImportBookingProps) => ShipmentService.import(file, userId),
      onSuccess: () => {},
    });
  };

  const useSendCartonLogs = (onSuccess?: () => void, onFailure?: (error: unknown) => void) => {
    return useMutation(
      ["booking.cartons.logs.send"],
      (logs: CartonLog[]) => {
        return CartonLogService.createList(logs);
      },
      {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (error) => {
          onFailure?.(error);
        },
      }
    );
  };

  const useSubmitBookingUnderReview = () => {
    return useMutation({
      mutationKey: BookingKeys.underReview(),
      mutationFn: ({
        id,
        booking,
        saveAndBook = false,
        cfsReceived = false,
        received = false,
        uuid = undefined,
        overrideChangedOptions,
      }: SaveBookingProps) => {
        return ShipmentBookingService.update(id || 0, {
          record: booking,
          confirmed: saveAndBook,
          cfsReceived,
          received,
          changedOptions: overrideChangedOptions || changedOptions,
          uuid,
        });
      },
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: [BookingKeys.all, "network"] });
        queryClient.invalidateQueries({ queryKey: [BookingKeys.all, variables.id] });

        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
    });
  };

  const useSaveBooking = (callback?: () => void) => {
    return useMutation({
      mutationKey: BookingKeys.save(),
      mutationFn: ({
        id,
        booking,
        saveAndBook = false,
        cfsReceived = false,
        received = false,
        uuid = undefined,
        overrideChangedOptions,
      }: SaveBookingProps) => {
        if (id)
          return ShipmentBookingService.update(id, {
            record: booking,
            confirmed: saveAndBook,
            cfsReceived,
            received,
            changedOptions: overrideChangedOptions || changedOptions,
            uuid,
          });

        return ShipmentBookingService.create({
          record: {
            ...booking,
            isBooking: true,
            companyId: currentUser?.companyId,
            projectId: isFMVersion ? booking.projectId : 23,
            eventType: isFMVersion ? booking.eventType : defaultEventType,
          },
          confirmed: saveAndBook,
          uuid,
        });
      },
      retry: 0,
      networkMode: "online",
      onMutate: () => setRequestingSaveBooking(true),
      onError: (error) => {
        setRequestingSaveBooking(false);
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown")
          setResponseError(errorAxios?.response?.data);
        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: (response, variables) => {
        const requestWasBlocked = response?.data?.type === TypeError.ERROR_WITHOUT_TOAST;

        if (requestWasBlocked) return;
        setRequestingSaveBooking(false);
        setResponseError(undefined);

        queryClient.invalidateQueries({ queryKey: [BookingKeys.all, "network"] });
        queryClient.invalidateQueries({ queryKey: [BookingKeys.all, variables.id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
        history.push(PRIVATE_ROUTES.BOOKINGS);
      },
    });
  };

  const useUpdateShipmentDocuments = (callback?: () => void) => {
    return useMutation({
      mutationKey: BookingKeys.updateDocuments(),
      mutationFn: ({ shipmentId, documents }: UpdateShipmentDocumentProps) =>
        ShipmentDocumentService.updateListByShipmentId(shipmentId, documents),
      onSuccess: () => {
        if (callback) callback();
      },
    });
  };

  const useUploadDocument = (onUploadSuccess: (shipmentDocument: ShipmentDocument) => void) =>
    useMutation({
      mutationKey: BookingKeys.uploadDocument(),
      mutationFn: (shipmentDocument: ShipmentDocument) =>
        ShipmentDocumentService.create(shipmentDocument),
      onSuccess: (response) => {
        if (response && response?.data) {
          onUploadSuccess(response?.data);
        }
      },
    });

  const useDownloadBookingFile = (onSuccess?: (data?: unknown) => void) => {
    return useMutation({
      mutationKey: BookingKeys.downloadBookingFile(),
      mutationFn: (bookingId: number) => ShipmentService.getBookingFile(bookingId),
      onSuccess: (response) => {
        if (response && response.data) {
          const dataStr = `data:text/json;charset=utf-8,[${encodeURIComponent(
            JSON.stringify(response.data)
          )}]`;

          const downloadAnchorNode = document.createElement("a");

          downloadAnchorNode.setAttribute(
            "download",
            `booking-${response.data.shipmentNumber}.json`
          );
          downloadAnchorNode.setAttribute("href", dataStr);

          document.body.appendChild(downloadAnchorNode); // required for firefox

          downloadAnchorNode.click();
          downloadAnchorNode.remove();

          onSuccess?.(response.data);
        }
      },
    });
  };

  const useDownloadBookingFiles = (callback?: (data?: unknown) => void) => {
    return useMutation({
      mutationKey: BookingKeys.downloadBookingFiles(),
      mutationFn: (bookingIds: number[]) => ShipmentService.getBookingFiles(bookingIds),
      onSuccess: (response) => {
        if (!response || !response.data) return;

        const blob = new Blob([new Uint8Array(response.data.content).buffer]);

        saveAs(blob, `${response.data.name}.${response.data.type}`);
        if (callback) callback();
      },
    });
  };

  const useSendBookingFilesMods = (callback?: () => void) => {
    return useMutation({
      mutationKey: BookingKeys.sendBookingFileMods(),
      mutationFn: (bookingIds: number[]) => ShipmentBookingService.sendBookingFilesMods(bookingIds),
      onSuccess: () => {
        NotificationToast.success(translate("bookingsSubmittedSuccessfully"));
        if (callback) callback();
      },
    });
  };

  const useDownloadAllDocumentsZip = () =>
    useMutation({
      mutationKey: BookingKeys.downloadDocuments(),
      mutationFn: ({ shipmentId, documents, hasBookingId }: ReportAllDocumentsProps) =>
        ShipmentReportService.generateAllDocumentsZip(shipmentId, documents, hasBookingId),
      onSuccess: (response) => {
        if (response && response.data && response.data.content) {
          handleGeneratePdf({
            data: response.data,
            exportFile: true,
          });
        }
      },
    });

  const useDownloadInvoicePackingDocument = () =>
    useMutation({
      mutationKey: BookingKeys.downloadCipl(),
      mutationFn: (shipmentId: number) =>
        ShipmentReportService.generateInvoicePackingPDFs(shipmentId),
    });

  const useDownloadCiplCSV = () =>
    useMutation({
      mutationKey: BookingKeys.downloadCiplCsv(),
      mutationFn: (shipmentId: number) =>
        ShipmentReportService.generateCiplCSV(shipmentId),
    });

  const useDownloadCartonLogs = () =>
    useMutation({
      mutationKey: BookingKeys.downloadCartonLogs(),
      mutationFn: (shipmentId: number) => CartonService.generateLog(shipmentId),
    });

  const useCancelBooking = () => {
    return useMutation({
      mutationKey: BookingKeys.cancel(),
      mutationFn: (id: number) => ShipmentService.cancelBooking(id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({ queryKey: [BookingKeys.all, id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
    });
  };

  const useReceiveAllBookings = () => {
    return useMutation({
      mutationKey: BookingKeys.receive(),
      mutationFn: ({ bookingIds }: { bookingIds: number[] }) =>
        ShipmentService.receivedAllBookings(bookingIds),
      onSuccess: (_, variables) => {
        for (const id of variables.bookingIds) {
          queryClient.invalidateQueries({ queryKey: [BookingKeys.all, id] });
        }
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.shipment.received.all.bookings",
        });
      },
    });
  };

  const useSendBookingReviewCfs = () => {
    return useMutation(
      ["review.cfs.booking"],
      (id: number) => ShipmentBookingService.reviewByCfs(id),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.shipment.review.cfs.booking",
          });
        },
      }
    );
  };

  return {
    getAllBookings,
    getBookingById,
    getExportBookingsById,
    getBookingsById,
    getCartonsByBooking,
    useImportBooking,
    useDownloadBookingFile,
    useDownloadBookingFiles,
    useDownloadInvoicePackingDocument,
    useDownloadCiplCSV,
    useDownloadCartonLogs,
    useDownloadAllDocumentsZip,
    useSaveBooking,
    useUploadDocument,
    useCancelBooking,
    useUpdateShipmentDocuments,
    getOrdersByBooking,
    useSendBookingFilesMods,
    useReceiveAllBookings,
    useSendCartonLogs,
    useSendBookingReviewCfs,
    requestingSaveBooking,
    useSubmitBookingUnderReview,
  };
};

export default useActionBooking;
